<script lang="ts" setup>
import {
  DefinitionParameterValueDto,
  NumericEvaluationParameterDto,
  instanceOfNumericEvaluationParameterDto,
} from '@/api/generator';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import Button from '@/components/buttons/Button.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { edit, error, sortUp, warning } from '@/components/icons/icons';
import { useProtocolsStore } from '@/stores/protocolsStore';
import { useUnitsStore } from '@/stores/unitsStore';
import { translateEntityProperty } from '@/utils/helper';
import { toDisplayValue, toRangeDisplay } from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings';
import { mdiDragHorizontal } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import { mapValues, values } from 'lodash';
import { useField } from 'vee-validate';
import { computed, onMounted, ref, toRef, watch } from 'vue';
import LimitField from './LimitField.vue';
import EnumOrQuantityLabel from './EnumOrQuantityLabel.vue';
import EvaluationCard from './EvaluationCard.vue';

interface EvaluationsQuantityTileProps {
  name: string; // evaluationScopes[{tableIndex = 0}].evaluationParameters[{index}]
  showInBeamConstancyCheck?: boolean;
}

const emits = defineEmits<{
  edit: [param: NumericEvaluationParameterDto];
  remove: [param: NumericEvaluationParameterDto];
  select: [];
}>();

const props = defineProps<EvaluationsQuantityTileProps>();

const mode = useFormMode();
const { t } = useTranslation();

const unitsStore = useUnitsStore();

const protocolsStore = useProtocolsStore();

onMounted(async () => {
  await unitsStore.initialize();
  await protocolsStore.initialize();
});

const { value: tileData } = useField<NumericEvaluationParameterDto>(toRef(() => props.name));

function handleRemove() {
  emits('remove', tileData.value);
}

function handleEdit() {
  emits('edit', tileData.value);
}

const checkBoxValuePath = computed(() => toRef(() => `${props.name}.isEnabled`));

const limits = {
  lowerFailed: 'lowerFailedLimit',
  lowerWarning: 'lowerWarningLimit',
  upperWarning: 'upperWarningLimit',
  upperFailed: 'upperFailedLimit',
};

const limitFields = mapValues(limits, (limit) =>
  useField<number | undefined | null>(toRef(() => `${props.name}.${limit}`)),
);

const rangeDisabled = computed(
  () => mode.value === 'details' || tileData.value.limitType === 'WithoutEvaluation',
);

function removeLimitValues() {
  values(limitFields).forEach((limit) => limit.setValue(null));
}

watch(
  () => tileData.value.limitType,
  (value) => {
    if (value === 'WithoutEvaluation') {
      removeLimitValues();
    }
  },
);

const inputRangeDescription = computed(() =>
  toRangeDisplay(
    tileData.value.min,
    tileData.value.max,
    tileData.value.limitUnitAbbreviation || undefined,
  ),
);

// A replaced definition collapses the details. The first id to arrive stays collapsed.
const showMoreKey = computed(() => tileData.value.definition?.id ?? 'no-definition');
const detailsExpanded = ref(false);

watch(showMoreKey, (id, previousId) => {
  if (previousId && previousId !== 'no-definition' && id !== previousId) {
    detailsExpanded.value = false;
  }
});

const showManualActions = computed(
  () => mode.value !== 'details' && !props.showInBeamConstancyCheck,
);

const showLimits = computed(() => {
  const limitType = tileData.value?.limitType;
  return limitType != null && limitType !== 'WithoutEvaluation';
});

const presetHeaders = [
  { key: 'title', align: 'start' as const, sortable: false },
  { key: 'value', align: 'end' as const, sortable: false },
];

const presetRows = computed(() => {
  const valueSet = tileData.value?.definitionParameterValueSet;
  const valuesInSet = valueSet?.values;
  if (!valuesInSet?.length) return [];

  const byIdentifier = new Map(valuesInSet.map((value) => [value.parameterIdentifier, value]));
  const used = new Set<string>();
  const rows = (tileData.value.definition?.definitionParameters ?? []).flatMap((parameter) => {
    const identifier = parameter.parameterIdentifier;
    if (!identifier) return [];
    used.add(identifier);
    return [
      {
        title: translateEntityProperty('name', parameter) || identifier,
        value: formatPresetValue(byIdentifier.get(identifier)),
      },
    ];
  });

  valuesInSet.forEach((value) => {
    if (used.has(value.parameterIdentifier)) return;
    rows.push({
      title: value.parameterIdentifier,
      value: formatPresetValue(value),
    });
  });

  rows.push({
    title: t('DefinitionParameterProtocol_one'),
    value: protocolsStore.getById(valueSet?.protocolId)?.name || EMPTY_FIELD_SIGN,
  });

  return rows;
});

function formatPresetValue(value: DefinitionParameterValueDto | undefined) {
  if (!value) return EMPTY_FIELD_SIGN;
  return toDisplayValue(value.value, {
    valueType: value.valueType,
    fractionDigits: value.decimalPlaces,
    unitAbbreviation: value.unitAbbreviation,
  });
}
</script>

<template>
  <EvaluationCard data-testid="EvaluationQuantityTile">
    <template #leading>
      <v-icon
        v-if="showManualActions"
        :icon="mdiDragHorizontal"
        class="handle cursor-move"
        data-testid="quantity-drag"
      />
      <VeeCheckbox
        v-if="showInBeamConstancyCheck"
        :name="checkBoxValuePath.value"
        :placeholder="$t('Required')"
        :mode="'edit'"
        :disabled="mode === 'details'"
        data-testid="quantity-enable"
        @change="emits('select')"
        @click.stop
      />
    </template>

    <template #title>
      {{ translateEntityProperty('name', tileData) }}
      <template v-if="instanceOfNumericEvaluationParameterDto(tileData)">
        {{ translateEntityProperty('name', tileData.geometry) }}
      </template>
    </template>

    <template v-if="showManualActions" #actions>
      <TextVBtn
        size="small"
        :icon="edit"
        color="secondary"
        data-testid="quantity-edit"
        @click="handleEdit"
      />
      <PtwDeleteButton data-testid="quantity-delete" @delete="handleRemove" />
    </template>

    <div class="quantity-column" :class="{ 'is-full': !showLimits }">
      <p class="section-heading">{{ $t('Quantity_one') }}</p>

      <div class="field-grid">
        <EnumOrQuantityLabel
          class="field-grid__span"
          :data="tileData"
          :label="$t('Quantity_one')"
        />

        <FormLabel v-if="tileData.evaluationType" :label="$t('EvaluationType_one')">
          {{ $t(`EvaluationTypeEnum.${tileData.evaluationType}` as I18nKey) }}
        </FormLabel>
        <FormLabel v-else :label="$t('EvaluationType_one')">{{ EMPTY_FIELD_SIGN }}</FormLabel>

        <FormLabel v-if="tileData.evaluationType" :label="$t('EvaluationUnit_one')">
          {{ tileData.evaluationUnitAbbreviation || EMPTY_FIELD_SIGN }}
        </FormLabel>
      </div>

      <div class="details-toggle">
        <Button
          variant="ghost"
          size="small"
          type="button"
          iconPosition="start"
          :icon="sortUp"
          :label="detailsExpanded ? $t('ShowLessDetails') : $t('ShowMoreDetails')"
          class="details-toggle__button"
          :class="{ 'is-collapsed': !detailsExpanded }"
          data-testid="quantity-details-toggle"
          @click="detailsExpanded = !detailsExpanded"
        />
      </div>

      <div
        class="details"
        :class="{ 'is-open': detailsExpanded }"
        data-testid="quantity-definition-details"
      >
        <div class="details__content">
          <div class="field-grid">
            <FormLabel :label="$t('Definition_one')">
              {{ translateEntityProperty('name', tileData.definition) }}
            </FormLabel>
            <FormLabel :label="$t('DefinitionUnit_one')">
              {{
                unitsStore.getById(tileData.definition?.unitId)?.abbreviation || EMPTY_FIELD_SIGN
              }}
            </FormLabel>
            <FormLabel :label="$t('DefinitionProtocol_one')">
              {{
                protocolsStore.getById(tileData.definition?.protocolId)?.name || EMPTY_FIELD_SIGN
              }}
            </FormLabel>
            <FormLabel :label="$t('ValueType')">
              {{
                tileData.definition?.valueType
                  ? $t(tileData.definition.valueType as I18nKey)
                  : EMPTY_FIELD_SIGN
              }}
            </FormLabel>
            <FormLabel v-if="tileData.evaluationType" :label="$t('InputRangeForExecution')">
              {{ inputRangeDescription }}
            </FormLabel>
            <FormLabel
              v-if="tileData.evaluationType && tileData.definition?.valueType !== 'IntegerNumber'"
              :label="$t('NumberOfDisplayedDecimals')"
            >
              {{ tileData.decimalPlaces }}
            </FormLabel>
          </div>

          <div v-if="presetRows.length" class="preset" data-testid="quantity-preset">
            <p class="subsection-heading">{{ $t('DefinitionParameterSet_one') }}</p>
            <v-data-table
              :headers="presetHeaders"
              :items="presetRows"
              :itemsPerPage="-1"
              hideDefaultFooter
              hideDefaultHeader
              class="preset-table"
            >
              <template #item.title="{ value }">
                <v-label class="text-justify">{{ value }}</v-label>
              </template>
              <template #item.value="{ value }">
                <span>{{ value }}</span>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>

      <div v-if="!showInBeamConstancyCheck" data-testid="quantity-instruction">
        <p class="section-heading">{{ $t('InstructionForTaskExecution' as I18nKey) }}</p>
        <VeeTextarea :label="$t('Instruction_one')" :name="`${name}.instruction`" :mode="mode" />
      </div>
    </div>

    <div v-if="showLimits" class="limits-column" data-testid="quantity-limits">
      <p class="section-heading">{{ $t('Limit_other') }}</p>
      <p class="section-hint">{{ $t('EvaluationLimitsHint' as I18nKey) }}</p>

      <FormLabel v-if="tileData.evaluationType" class="limit-type" :label="$t('LimitTypeLabel')">
        {{ $t(`LimitType.${tileData.limitType}` as I18nKey) || EMPTY_FIELD_SIGN }}
      </FormLabel>

      <div class="limits-grid">
        <LimitField
          :name="`${name}.lowerFailedLimit`"
          :label="$t('LowerFailedLimit')"
          :mode="mode"
          :disabled="rangeDisabled"
          :icon="error"
          :suffix="tileData?.evaluationUnitAbbreviation"
          :placeholder="showInBeamConstancyCheck ? $t('Required') : ''"
          :numberOfDisplayedDecimals="tileData.decimalPlaces"
          :valueType="tileData.definition?.valueType"
          :evaluationType="tileData.evaluationType"
          iconColor="RED"
        />
        <LimitField
          :name="`${name}.lowerWarningLimit`"
          :label="$t('LowerWarningLimit')"
          :mode="mode"
          :disabled="rangeDisabled"
          :icon="warning"
          :suffix="tileData?.evaluationUnitAbbreviation"
          :numberOfDisplayedDecimals="tileData.decimalPlaces"
          :valueType="tileData.definition?.valueType"
          :evaluationType="tileData.evaluationType"
          iconColor="YELLOW"
        />
        <LimitField
          :name="`${name}.upperWarningLimit`"
          :label="$t('UpperWarningLimit')"
          :mode="mode"
          :disabled="rangeDisabled"
          :icon="warning"
          :suffix="tileData?.evaluationUnitAbbreviation"
          :numberOfDisplayedDecimals="tileData.decimalPlaces"
          :valueType="tileData.definition?.valueType"
          :evaluationType="tileData.evaluationType"
          iconColor="YELLOW"
        />
        <LimitField
          :name="`${name}.upperFailedLimit`"
          :label="$t('UpperFailedLimit')"
          :mode="mode"
          :disabled="rangeDisabled"
          :icon="error"
          :suffix="tileData?.evaluationUnitAbbreviation"
          :placeholder="showInBeamConstancyCheck ? $t('Required') : ''"
          :numberOfDisplayedDecimals="tileData.decimalPlaces"
          :valueType="tileData.definition?.valueType"
          :evaluationType="tileData.evaluationType"
          iconColor="RED"
        />
      </div>
    </div>
  </EvaluationCard>
</template>

<style lang="scss" scoped>
.quantity-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.quantity-column.is-full {
  grid-column: 1 / -1;
}

.section-heading {
  margin: 0;
  color: rgb(var(--v-theme-primary-darken-2));
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.subsection-heading {
  margin: 0 0 8px;
  color: rgb(var(--v-theme-primary-darken-2));
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.section-hint {
  margin: 8px 0 16px;
  color: rgb(var(--v-theme-primary));
  font-size: 14px;
  line-height: 20px;
}

.field-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px 32px;
}

.field-grid__span {
  grid-column: 1 / -1;
}

.field-grid__span :deep(.text-body-1) {
  font-size: 14px;
  line-height: 20px;
}

.details-toggle {
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgb(var(--v-theme-primary-lighten-3));
}

.details-toggle__button {
  color: rgb(var(--v-theme-primary-darken-2)) !important;
}

.details-toggle__button:hover {
  color: rgb(var(--v-theme-primary-lighten-1)) !important;
}

.details {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 300ms ease;
}

.details.is-open {
  grid-template-rows: 1fr;
}

.details__content {
  overflow: hidden;
}

.details-toggle__button :deep(.v-icon) {
  transition: transform 300ms ease;
}

.details-toggle__button.is-collapsed :deep(.v-icon) {
  transform: rotate(0.5turn);
}

@media (prefers-reduced-motion: reduce) {
  .details,
  .details-toggle__button :deep(.v-icon) {
    transition: none;
  }
}

.preset-table :deep(.v-data-table__td) {
  height: unset !important;
  min-height: unset !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  padding-left: 0 !important;
  vertical-align: top;
}

.preset {
  margin-top: 16px;
}

.limit-type {
  margin-bottom: 16px;
}

.limits-column {
  container-type: inline-size;
}

.limits-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  align-items: end;
}

@container (max-width: 40rem) {
  .limits-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
